import {
    Shield24Regular,
    LockClosed24Regular,
    Database24Regular,
    Share24Regular,
    Globe24Regular,
    DocumentText24Regular,
    Scales24Regular,
    Mail24Regular,
    Bot24Regular,
    Phone24Regular
} from "@fluentui/react-icons";
import styles from "./PrivacyPolicy.module.css";

const PrivacyPolicyText = () => {
    return (
        <main className={styles.pages}>
            <h1>Privacy Policy</h1>
            <p className={styles.subtitle}>Effective Date: May 20, 2026</p>

            {/* General Privacy Policy */}
            <section>
                <h2>
                    <Shield24Regular className={styles.sectionIcon} />
                    General Privacy Policy
                </h2>
                <p>
                    Thank you for visiting basebuddy.ai (the "Site"), operated by Strategi Consulting, LLC ("us", "we", or "our"). This page informs
                    you of our policies regarding the collection, use, and disclosure of information we receive from users of the Site.
                </p>

                <h3>Information Collection and Use</h3>
                <ul>
                    <li>We do not collect personally identifiable information unless voluntarily provided.</li>
                    <li>Any voluntarily provided personal information is used only for its intended purpose.</li>
                </ul>

                <h3>Log Data</h3>
                <ul>
                    <li>We may collect non-identifiable analytics such as IP address, browser type, timestamps, and aggregate statistics.</li>
                </ul>

                <h3>Cookies</h3>
                <ul>
                    <li>Cookies may be used to improve user experience. You may disable cookies, but parts of the site may not function fully.</li>
                </ul>

                <h3>Security</h3>
                <ul>
                    <li>We implement reasonable measures to prevent unauthorized access, alteration, or disclosure of personal information.</li>
                </ul>

                <h3>Changes to This Privacy Policy</h3>
                <ul>
                    <li>This Policy is effective as of May 20, 2026.</li>
                    <li>Updates will be posted on this page and are effective immediately.</li>
                    <li>We reserve the right to update this Policy at any time.</li>
                    <li>Continued use of the Site constitutes acceptance of modifications.</li>
                </ul>
            </section>

            {/* Base Buddy AI-Driven Chatbot */}
            <section>
                <h2>
                    <Bot24Regular className={styles.sectionIcon} />
                    Base Buddy AI-Driven Chatbot
                </h2>
                <p>
                    Base Buddy uses Microsoft Azure OpenAI Service. Your use is subject to this Privacy Policy and the Microsoft Product and Services
                    Data Protection Addendum. Strategi Consulting, LLC is not responsible for Microsoft's policies.
                </p>
            </section>

            {/* Information Collected */}
            <section>
                <h2>
                    <Database24Regular className={styles.sectionIcon} />
                    Information Collected
                </h2>

                <h3>User Input or Output Content</h3>
                <ul>
                    <li>Logs may be used to improve system performance. They contain no personal information.</li>
                </ul>

                <h3>User Information</h3>
                <ul>
                    <li>Non-personal usage patterns and session data are collected for analytics and optimization.</li>
                </ul>
            </section>

            {/* SMS Messaging Data */}
            <section>
                <h2>
                    <Phone24Regular className={styles.sectionIcon} />
                    SMS Messaging Data
                </h2>
                <p>When you opt in to Base Buddy SMS, we collect and process:</p>
                <ul>
                    <li>Your mobile phone number (to deliver messages)</li>
                    <li>The content of messages you send us (to provide relevant responses)</li>
                    <li>Metadata about message delivery (timestamps, delivery status)</li>
                    <li>Your base affiliation, if provided during sign-up</li>
                </ul>
                <p>
                    <strong>
                        We do not share your mobile phone number or message content with third parties for marketing purposes.
                    </strong>{" "}
                    We may share information with service providers (such as our SMS delivery provider, Twilio) solely to operate the
                    service, and with U.S. government customers under the terms of our federal contracts. SMS data is retained according to
                    our standard data retention policy, which may be extended to meet federal record-keeping requirements.
                </p>
                <p>
                    You can opt out of SMS at any time by texting <strong>STOP</strong>. Opt-out removes your phone number from active
                    messaging lists but does not delete prior message history retained for service records. For full SMS terms, see our{" "}
                    <a href="#/sms_terms">SMS Terms & Consent</a> page.
                </p>
            </section>

            {/* Data Storage and Security */}
            <section>
                <h2>
                    <LockClosed24Regular className={styles.sectionIcon} />
                    Data Storage and Security
                </h2>

                <h3>Log Data</h3>
                <ul>
                    <li>Chat logs are stored securely and accessible only to authorized personnel.</li>
                </ul>

                <h3>Security</h3>
                <ul>
                    <li>
                        We use reasonable safeguards; however, no transmission is completely secure. Microsoft may temporarily store data to detect
                        abuse.
                    </li>
                </ul>
            </section>

            {/* Use of Information */}
            <section>
                <h2>
                    <DocumentText24Regular className={styles.sectionIcon} />
                    Use of Information
                </h2>

                <h3>Service Improvement</h3>
                <ul>
                    <li>Used to enhance performance and functionality.</li>
                    <li>Inputs/outputs are not shared with Microsoft or OpenAI for training.</li>
                </ul>

                <h3>Analytics</h3>
                <ul>
                    <li>Used to understand behavior and optimize the system.</li>
                </ul>

                <h3>System Maintenance</h3>
                <ul>
                    <li>Used for debugging, security, and reliability.</li>
                </ul>
            </section>

            {/* Information Sharing */}
            <section>
                <h2>
                    <Share24Regular className={styles.sectionIcon} />
                    Information Sharing
                </h2>

                <h3>Third Parties</h3>
                <ul>
                    <li>We do not share information except as legally required.</li>
                </ul>

                <h3>Aggregate Data</h3>
                <ul>
                    <li>May be shared in anonymized form for research.</li>
                </ul>
            </section>

            {/* Cookies and Tracking */}
            <section>
                <h2>
                    <Globe24Regular className={styles.sectionIcon} />
                    Cookies and Tracking
                </h2>
                <p>Cookies are used for authentication and session management.</p>
                <p>Links to other websites may be provided; we are not responsible for the privacy practices of linked sites.</p>
            </section>

            {/* User Consent */}
            <section>
                <h2>User Consent</h2>
                <ul>
                    <li>You may withdraw consent at any time.</li>
                    <li>You may disable cookies to opt out of non-essential data collection.</li>
                </ul>
            </section>

            {/* Legal Compliance */}
            <section>
                <h2>
                    <Scales24Regular className={styles.sectionIcon} />
                    Legal Compliance
                </h2>
                <ul>
                    <li>We comply with applicable privacy laws.</li>
                    <li>We use appropriate technical and organizational safeguards.</li>
                </ul>
            </section>

            {/* Changes */}
            <section>
                <h2>Changes to These Terms</h2>
                <p>Updates will be posted, and are effective upon posting.</p>
            </section>

            {/* Contact */}
            <section>
                <h2>
                    <Mail24Regular className={styles.sectionIcon} />
                    Contact Us
                </h2>
                <p>
                    For questions, contact <a href="mailto:hello@basebuddy.ai">hello@basebuddy.ai</a>.
                </p>
            </section>
        </main>
    );
};

export default PrivacyPolicyText;
